/**
 * Documents Page
 *
 * Lists project documents grouped by status (Published, Drafts, Archived).
 * Supports filtering by doc_type and creating new documents.
 */

import { Component, createSignal, createMemo, Show, For } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { Button, EmptyState, Spinner, Select } from "../components/ui";
import { ImportFromGithubModal } from "../components/documents/ImportFromGithubModal";
import { useProjectId } from "../contexts/ProjectContext";
import { useToast, useApiWithSource } from "../hooks";
import {
  listDocuments,
  createDocument,
  type Document,
  type DocumentType,
  type CreateDocumentRequest,
} from "../api";

const DOC_TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "roadmap", label: "Roadmap" },
  { value: "architecture", label: "Architecture" },
  { value: "adr", label: "ADR" },
  { value: "design", label: "Design" },
  { value: "guide", label: "Guide" },
  { value: "custom", label: "Custom" },
];

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  roadmap: "Roadmap",
  architecture: "Architecture",
  adr: "ADR",
  design: "Design",
  guide: "Guide",
  custom: "Custom",
};

// A doc type is a taxonomy label, not a signal: none of the six carries
// success, warning, error or AI meaning, so they all take the neutral chip.
const DOC_TYPE_CHIP = "bg-sunken text-ink-2 border-line";

const DOC_TYPE_COLORS: Record<DocumentType, string> = {
  roadmap: DOC_TYPE_CHIP,
  architecture: DOC_TYPE_CHIP,
  adr: DOC_TYPE_CHIP,
  design: DOC_TYPE_CHIP,
  guide: DOC_TYPE_CHIP,
  custom: DOC_TYPE_CHIP,
};

const DocTypeBadge: Component<{ type: DocumentType }> = (props) => {
  return (
    <span
      class={
        "inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border " +
        DOC_TYPE_COLORS[props.type]
      }
    >
      {DOC_TYPE_LABELS[props.type]}
    </span>
  );
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return diffMins <= 1 ? "just now" : `${diffMins}m ago`;
    }
    return `${diffHours}h ago`;
  }
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

const DocumentRow: Component<{
  doc: Document;
  onClick: () => void;
}> = (props) => {
  return (
    <button
      data-document-row
      class="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-sunken transition-colors border-b border-line last:border-b-0"
      onClick={props.onClick}
    >
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-ink truncate">
            {props.doc.title}
          </span>
          <Show when={props.doc.is_stale}>
            <span
              class="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-signal-amber-tint text-signal-amber border border-signal-amber/25"
              title={
                props.doc.stale_since
                  ? `Updated in Git on ${formatDate(props.doc.stale_since)}`
                  : "Updated in Git"
              }
            >
              <svg
                class="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              Stale
            </span>
          </Show>
        </div>
        <div class="flex items-center gap-2 mt-1">
          <DocTypeBadge type={props.doc.doc_type} />
          <span class="text-xs text-ink-3">v{props.doc.version}</span>
        </div>
      </div>
      <div class="text-xs text-ink-3 whitespace-nowrap">
        {formatDate(props.doc.updated_at)}
      </div>
      <svg
        class="w-4 h-4 text-ink-3 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5l7 7-7 7"
        />
      </svg>
    </button>
  );
};

const StatusGroup: Component<{
  label: string;
  docs: Document[];
  defaultCollapsed?: boolean;
  onDocClick: (doc: Document) => void;
}> = (props) => {
  const [collapsed, setCollapsed] = createSignal(
    props.defaultCollapsed ?? false,
  );

  return (
    <Show when={props.docs.length > 0}>
      <div class="mb-4">
        <button
          class="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-sunken transition-colors"
          onClick={() => setCollapsed(!collapsed())}
        >
          <svg
            class={
              "w-4 h-4 text-ink-3 transition-transform " +
              (collapsed() ? "" : "rotate-90")
            }
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
          <span class="text-sm font-semibold text-ink-2">{props.label}</span>
          <span class="text-xs text-ink-3">({props.docs.length})</span>
        </button>
        <Show when={!collapsed()}>
          <div class="bg-surface rounded border border-line mx-4 overflow-hidden">
            <For each={props.docs}>
              {(doc) => (
                <DocumentRow doc={doc} onClick={() => props.onDocClick(doc)} />
              )}
            </For>
          </div>
        </Show>
      </div>
    </Show>
  );
};

const CreateDocumentForm: Component<{
  onSubmit: (req: CreateDocumentRequest) => void;
  onCancel: () => void;
  loading: boolean;
}> = (props) => {
  const [title, setTitle] = createSignal("");
  const [docType, setDocType] = createSignal<DocumentType>("custom");

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    const trimmed = title().trim();
    if (!trimmed) return;
    props.onSubmit({ title: trimmed, doc_type: docType() });
  };

  return (
    <div class="mx-4 mb-4 bg-surface rounded border border-line p-4">
      <form onSubmit={handleSubmit}>
        <div class="mb-3">
          <label class="block text-sm font-medium text-ink-2 mb-1">Title</label>
          <input
            type="text"
            class="w-full px-3 py-2 text-sm border border-line rounded focus-ring"
            placeholder="Document title..."
            value={title()}
            onInput={(e) => setTitle(e.currentTarget.value)}
            autofocus
          />
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-ink-2 mb-1">Type</label>
          <Select
            value={docType()}
            onChange={(v) => setDocType(v as DocumentType)}
            options={DOC_TYPE_OPTIONS.filter((o) => o.value !== "")}
          />
        </div>
        <div class="flex items-center gap-2 justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={props.onCancel}
            disabled={props.loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            loading={props.loading}
            disabled={!title().trim()}
          >
            Create
          </Button>
        </div>
      </form>
    </div>
  );
};

export const DocumentsPage: Component = () => {
  const navigate = useNavigate();
  const projectId = useProjectId();
  const { showError, showSuccess } = useToast();

  const [typeFilter, setTypeFilter] = createSignal("");
  const [staleOnly, setStaleOnly] = createSignal(false);
  const [showCreate, setShowCreate] = createSignal(false);
  const [creating, setCreating] = createSignal(false);
  const [showImport, setShowImport] = createSignal(false);

  const {
    data: response,
    loading,
    error,
    refetch,
  } = useApiWithSource(projectId, (pid) => listDocuments(pid));

  // Group documents by status, with optional type and stale filters
  const filteredDocs = createMemo(() => {
    const items = response()?.items ?? [];
    const tf = typeFilter();
    const so = staleOnly();
    return items.filter((d) => {
      if (tf && d.doc_type !== tf) return false;
      if (so && !d.is_stale) return false;
      return true;
    });
  });

  const publishedDocs = createMemo(() =>
    filteredDocs().filter((d) => d.status === "published"),
  );

  const draftDocs = createMemo(() =>
    filteredDocs().filter((d) => d.status === "draft"),
  );

  const archivedDocs = createMemo(() =>
    filteredDocs().filter((d) => d.status === "archived"),
  );

  const staleCount = createMemo(
    () => (response()?.items ?? []).filter((d) => d.is_stale).length,
  );

  const handleCreate = async (req: CreateDocumentRequest) => {
    setCreating(true);
    try {
      await createDocument(projectId(), req);
      showSuccess("Document created");
      setShowCreate(false);
      refetch();
    } catch {
      showError("Failed to create document");
    } finally {
      setCreating(false);
    }
  };

  const handleDocClick = (doc: Document) => {
    navigate(`/documents/${doc.id}`);
  };

  return (
    <div class="h-full overflow-auto">
      {/* Header */}
      <div class="px-4 py-4 border-b border-line bg-surface">
        <div class="flex items-center justify-between mb-3">
          <div>
            <h1 class="text-lg font-semibold text-ink">Documents</h1>
            <p class="text-sm text-ink-3 mt-0.5">
              Project knowledge base — design docs, roadmaps, and guides
            </p>
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowImport(true)}
            >
              Import from GitHub
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowCreate(true)}
            >
              + New Document
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <div class="flex items-center gap-3">
          <div class="w-48">
            <Select
              value={typeFilter()}
              onChange={(v) => setTypeFilter(v)}
              options={DOC_TYPE_OPTIONS}
            />
          </div>
          <Show when={staleCount() > 0}>
            <button
              type="button"
              onClick={() => setStaleOnly((v) => !v)}
              class={`text-xs rounded-full px-2 py-0.5 border transition-colors focus-ring ${staleOnly() ? "bg-ink border-ink text-paper font-medium" : "bg-signal-amber-tint border-signal-amber/25 text-signal-amber hover:bg-signal-amber/15"}`}
              aria-pressed={staleOnly()}
            >
              {staleCount()} document{staleCount() !== 1 ? "s" : ""} updated in
              GitHub
              <Show when={staleOnly()}> &#x2715;</Show>
            </button>
          </Show>
        </div>
      </div>

      {/* Content */}
      <div class="py-4">
        {/* Loading */}
        <Show when={loading()}>
          <div class="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Show>

        {/* Error */}
        <Show when={error()}>
          <div class="mx-4 bg-signal-red-tint border border-signal-red/25 rounded p-4 text-signal-red text-sm">
            Failed to load documents.{" "}
            <button
              class="underline hover:no-underline"
              onClick={() => refetch()}
            >
              Try again
            </button>
          </div>
        </Show>

        {/* Create form */}
        <Show when={showCreate()}>
          <CreateDocumentForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreate(false)}
            loading={creating()}
          />
        </Show>

        {/* Document list */}
        <Show when={!loading() && !error() && response()}>
          <Show
            when={filteredDocs().length > 0}
            fallback={
              <EmptyState
                title="No documents yet"
                description="Create your first document to build your project's knowledge base."
                action={
                  <Button variant="primary" onClick={() => setShowCreate(true)}>
                    Create Document
                  </Button>
                }
              />
            }
          >
            <StatusGroup
              label="Published"
              docs={publishedDocs()}
              onDocClick={handleDocClick}
            />
            <StatusGroup
              label="Drafts"
              docs={draftDocs()}
              onDocClick={handleDocClick}
            />
            <StatusGroup
              label="Archived"
              docs={archivedDocs()}
              defaultCollapsed={true}
              onDocClick={handleDocClick}
            />
          </Show>
        </Show>
      </div>

      <ImportFromGithubModal
        open={showImport()}
        onClose={() => setShowImport(false)}
        projectId={projectId()}
        onImportComplete={() => {
          showSuccess("Documents imported");
          refetch();
        }}
      />
    </div>
  );
};
